import { NoteMention, NoteTextSegment } from '../account-notes/account-notes.types';

// The text side of an `@` mention in a comment field. Pure functions, no DOM, so the rules are
// unit-tested on their own.

/** The `@…` being typed before the caret. */
export interface MentionQuery {
    /** Index of the `@`. */
    start: number;
    /** What follows the `@` up to the caret. */
    query: string;
}

/** Letters that can belong to a name, so `@Anna Meierx` is no longer the mention `@Anna Meier`. */
export const NAME_CHAR = /[\wÀ-ɏ]/;

/**
 * The mention being typed: the last `@` before the caret, with no space or line break after it — the
 * CPMM annotations' rule.
 */
export function mentionQueryAt(text: string, caret: number): MentionQuery | null {
    const before = text.slice(0, caret);
    const start = before.lastIndexOf('@');
    if (start === -1) {
        return null;
    }
    const query = before.slice(start + 1);
    return /\s/.test(query) ? null : { start, query };
}

/** Replaces the typed `@que` with the whole name and a space; the caret goes after the space. */
export function insertMention(
    text: string,
    query: MentionQuery,
    caret: number,
    displayName: string,
): { text: string; caret: number } {
    const token = `@${displayName} `;
    return {
        text: text.slice(0, query.start) + token + text.slice(caret),
        caret: query.start + token.length,
    };
}

/**
 * The mentions whose `@Name` still stands whole in the text. Typing inside a name, or deleting part of
 * it, breaks it — the name turns back into plain text and that person is no longer mentioned.
 */
export function keptMentions(text: string, mentions: NoteMention[]): NoteMention[] {
    const seen = new Set<string>();
    return mentions.filter((m) => {
        if (seen.has(m.userId) || !containsWhole(text, `@${m.displayName}`)) {
            return false;
        }
        seen.add(m.userId);
        return true;
    });
}

/** Backspace right after a mention removes the whole name. `null` ⇒ the caret is not at a mention's end. */
export function removeMentionBefore(
    text: string,
    caret: number,
    mentions: NoteMention[],
): { text: string; caret: number } | null {
    for (const m of mentions) {
        const token = `@${m.displayName}`;
        const start = caret - token.length;
        if (start >= 0 && text.slice(start, caret) === token) {
            return { text: text.slice(0, start) + text.slice(caret), caret: start };
        }
    }
    return null;
}

/**
 * Plain text and mentions, in order — for the read view and for the coloured layer behind the field.
 * With one capture group `String.split` puts the matches at the odd indexes, so empty parts are dropped
 * only after the flag is set.
 */
export function mentionSegments(text: string, mentions: { displayName: string }[]): NoteTextSegment[] {
    const names = mentions.map((m) => m.displayName).sort((a, b) => b.length - a.length);
    if (names.length === 0) {
        return [{ text, mention: false }];
    }
    // Same boundary as `keptMentions`: `@Anna Meier` is no mention inside `@Anna Meierhans`.
    const pattern = new RegExp(`(@(?:${names.map(escapeRegExp).join('|')}))(?!${NAME_CHAR.source})`, 'g');
    const parts = text.split(pattern);
    const out: NoteTextSegment[] = [];
    for (let i = 0; i < parts.length; i++) {
        if (parts[i] !== '') {
            out.push({ text: parts[i], mention: i % 2 === 1 });
        }
    }
    return out;
}

function containsWhole(text: string, token: string): boolean {
    let from = text.indexOf(token);
    while (from !== -1) {
        const next = text.charAt(from + token.length);
        if (next === '' || !NAME_CHAR.test(next)) {
            return true;
        }
        from = text.indexOf(token, from + 1);
    }
    return false;
}

export function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
